/**
 * Tenant Context Provider
 *
 * Provides multi-tenancy support across the application by:
 * 1. Loading tenant configuration from Firestore based on user's tenantId
 * 2. Providing tenant data to all components via React Context
 * 3. Handling loading and error states gracefully
 *
 * FIRESTORE COLLECTION: tenants/{tenantId}
 *
 * USAGE:
 * - Wrap your app with <TenantProvider> in main.tsx
 * - Access tenant data with useTenant() hook
 *
 * @see src/types/tenant.types.ts for type definitions
 * @see CLAUDE.md Architecture Standards for usage rules
 */

import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import {
  TenantConfig,
  TenantContextValue,
  DEFAULT_FEATURE_FLAGS,
  UNCONFIGURED_TENANT_ID,
  isTenantConfig,
} from '@/types/tenant.types';
import { applyTenantTheme, applyDefaultTheme } from '@/utils/theme';
import { cacheTenantLocale } from '@/utils/locale';
import { cacheTenantCurrency } from '@/utils/currency';
import { cacheTenantTimezone } from '@/utils/timezone';
import { cacheTenantTaxRate } from '@/utils/taxRate';
import { cacheTenantFeaturePacks } from '@/utils/featurePacks';
import { cacheOwnerIds } from '@/utils/access';
import { findUndeclaredTenantKeys } from '@/types/tenant.types';
import { getTenantIdSync } from '@/lib/tenant';

/**
 * Dev-only drift detector for the tenant document.
 *
 * `tenants/{tenantId}` is WORLD-READABLE (`allow read: if true` — the
 * pre-auth landing page needs branding), so any field on it is public. A
 * Cloud Function doing `.set({...}, { merge: true })` can add a key that no
 * TypeScript type describes and no reviewer sees. That is exactly how
 * `proposalSenderDomain` lived on a public document undeclared for months —
 * found by a security audit in July 2026, not by any control.
 *
 * The unit test (`src/types/__tests__/tenant-config-keys.test.ts`) pins a
 * captured shape, which only catches drift when someone updates the fixture.
 * THIS is the continuous half: it fires against real production data the
 * moment any developer loads the app.
 *
 * Deliberately:
 *   - dev-only (`import.meta.env.DEV`) — zero cost and zero noise in prod
 *   - wrapped in try/catch and never rethrows — this sits on the tenant load
 *     path, which gates the entire app including login. A drift *warning*
 *     must never be able to prevent sign-in.
 *   - `console.warn`, not `error` — it's a heads-up for a developer, not a
 *     runtime fault.
 */
function warnOnUndeclaredTenantKeys(data: unknown): void {
  if (!import.meta.env.DEV) return;
  try {
    const undeclared = findUndeclaredTenantKeys(data);
    if (undeclared.length === 0) return;
    console.warn(
      `[TenantContext] Tenant document carries ${undeclared.length} field(s) not declared on TenantConfig: ` +
        `${undeclared.join(', ')}.\n` +
        'The tenant doc is PUBLICLY READABLE — confirm nothing sensitive was added, then declare ' +
        'the field in src/types/tenant.types.ts (the build will require adding it to _exhaustive too) ' +
        'and update the fixture in src/types/__tests__/tenant-config-keys.test.ts. ' +
        'See SECURITY_FIX_PLAN.md §2A.',
    );
  } catch {
    // Never let a diagnostic break tenant load — see the note above.
  }
}

/*
 * Pre-login (e.g. the landing page) getTenantIdSync (@/lib/tenant) falls
 * back to DEFAULT_TENANT_ID so the Firestore subscription has SOMETHING
 * to read.
 *
 * TODO(saas-readiness): hardcoded 'techery-au' default — for SaaS this
 * should be either (a) null + landing-page bypasses the tenant subscription
 * entirely, or (b) resolved from subdomain / custom domain via
 * tenant.domain.customDomain. Today: a user from a different tenant briefly
 * subscribes to techery-au's doc before localStorage gets populated at
 * login. See SaaS-readiness audit.
 */

/**
 * Sentinel tenant ID used by FALLBACK_TENANT_CONFIG.
 *
 * Canonical definition moved to `@/types/tenant.types` so leaf utilities
 * (e.g. src/utils/access.ts) can read it without a circular import.
 * Re-exported here for existing consumers.
 */
export { UNCONFIGURED_TENANT_ID };

/**
 * Neutral fallback tenant configuration.
 *
 * Loaded when the tenant's Firestore doc is missing, malformed, or unreadable.
 * Carries NO brand identity, NO Techery references, ALL feature flags off,
 * neutral theme. Seeing tenant.id === UNCONFIGURED_TENANT_ID is a
 * configuration-error state, never normal operation.
 *
 * TODO(saas-readiness): App.tsx / AuthGuard should refuse to render children
 * when this fallback is active, surfacing a clear "Account not configured"
 * error UI instead of letting components run with empty branding. For now the
 * neutral values prevent silent Techery brand leak to a misconfigured tenant.
 * See SaaS-readiness audit (May 2026).
 */
const FALLBACK_TENANT_CONFIG: TenantConfig = {
  id: UNCONFIGURED_TENANT_ID,
  companyName: 'Account not configured',
  companyAcronym: '',
  companyTagline: '',
  companyTaglineExpanded: '',
  theme: {
    // Neutral slate/gray palette — NOT Techery navy/gold (#1a2238 / #c5a065).
    // DEFAULT_THEME still ships brand colors as a tenant-creation starting
    // point; this fallback must not inherit them when no real tenant loaded.
    primaryColor: '#1f2937',
    secondaryColor: '#9ca3af',
    accentColor: '#e5e7eb',
    darkColor: '#111827',
    successColor: '#10b981',
    dangerColor: '#ef4444',
    warningColor: '#f59e0b',
  },
  branding: {
    logoUrl: undefined,
    faviconUrl: undefined,
    logoSvg: undefined,
  },
  business: {
    abn: undefined,
    address: undefined,
    phone: undefined,
    email: undefined,
    website: undefined,
    defaultTaxRate: 0.10,
    currency: 'AUD',
    timezone: 'Australia/Sydney',
  },
  featureFlags: {
    ...DEFAULT_FEATURE_FLAGS,
    // All flags explicitly off in fallback. A misconfigured tenant must not
    // get access to any feature surface — including legacy BP proposals which
    // DEFAULT_FEATURE_FLAGS leaves on during the BP→native parallel window.
    enableLegacyBetterProposals: false,
    useNativeProposals: false,
    proposalBuilderV2: false,
  },
  domain: {
    emailDomain: '',
  },
  isActive: false,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

/**
 * React Context for tenant data
 */
export const TenantContext = createContext<TenantContextValue | undefined>(undefined);

/**
 * Props for TenantProvider component
 */
interface TenantProviderProps {
  children: ReactNode;
  /** Optional override for tenant ID (useful for testing) */
  tenantIdOverride?: string;
}

/**
 * TenantProvider Component
 *
 * Wraps the application and provides tenant configuration to all child components.
 *
 * Flow:
 * 1. Check localStorage for logged-in user's tenantId
 * 2. If no tenantId, use DEFAULT_TENANT_ID
 * 3. Subscribe to tenant document in Firestore
 * 4. Provide tenant data via context
 *
 * @example
 * // In main.tsx
 * <TenantProvider>
 *   <App />
 * </TenantProvider>
 *
 * // With override for testing
 * <TenantProvider tenantIdOverride="test-tenant">
 *   <App />
 * </TenantProvider>
 */
export function TenantProvider({ children, tenantIdOverride }: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get the current tenant ID from localStorage or use default
   */
  const getTenantId = useCallback((): string => {
    // Allow override for testing
    if (tenantIdOverride) {
      return tenantIdOverride;
    }

    // tenant_id from localStorage (set during login), falling back to the
    // canonical default pre-login — see the TODO(saas-readiness) note above.
    return getTenantIdSync();
  }, [tenantIdOverride]);

  /**
   * Load tenant configuration from Firestore
   */
  const loadTenant = useCallback(async (tenantId: string): Promise<TenantConfig | null> => {
    console.log(`[TenantContext] Loading tenant config for: ${tenantId}`);

    try {
      const tenantDocRef = doc(db, 'tenants', tenantId);
      const tenantSnapshot = await getDoc(tenantDocRef);

      if (tenantSnapshot.exists()) {
        const data = {
          id: tenantSnapshot.id,
          ...tenantSnapshot.data(),
        };

        warnOnUndeclaredTenantKeys(data);

        if (isTenantConfig(data)) {
          console.log(`[TenantContext] Loaded tenant: ${data.companyName}`);
          return data;
        } else {
          console.warn('[TenantContext] Invalid tenant config structure:', data);
          return null;
        }
      } else {
        console.warn(`[TenantContext] Tenant not found: ${tenantId}`);
        return null;
      }
    } catch (err) {
      console.error('[TenantContext] Error loading tenant:', err);
      throw err;
    }
  }, []);

  /**
   * Refresh tenant configuration
   */
  const refreshTenant = useCallback(async (): Promise<void> => {
    const tenantId = getTenantId();
    setIsLoading(true);
    setError(null);

    try {
      const loadedTenant = await loadTenant(tenantId);

      if (loadedTenant) {
        setTenant(loadedTenant);
      } else {
        // Tenant doc missing or invalid — load neutral fallback. This is an
        // error state, not normal operation. See FALLBACK_TENANT_CONFIG.
        console.error('[TenantContext] Tenant config missing or invalid — using neutral fallback. This is a configuration error.');
        setTenant(FALLBACK_TENANT_CONFIG);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tenant';
      setError(errorMessage);
      console.error('[TenantContext] Firestore read failed — using neutral fallback:', errorMessage);
      setTenant(FALLBACK_TENANT_CONFIG);
    } finally {
      setIsLoading(false);
    }
  }, [getTenantId, loadTenant]);

  /**
   * Set up real-time listener for tenant changes
   */
  useEffect(() => {
    const tenantId = getTenantId();
    console.log(`[TenantContext] Setting up listener for tenant: ${tenantId}`);

    setIsLoading(true);
    setError(null);

    // Subscribe to tenant document for real-time updates
    const tenantDocRef = doc(db, 'tenants', tenantId);
    const unsubscribe = onSnapshot(
      tenantDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = {
            id: snapshot.id,
            ...snapshot.data(),
          };

          warnOnUndeclaredTenantKeys(data);

          if (isTenantConfig(data)) {
            console.log(`[TenantContext] Tenant updated: ${data.companyName}`);
            setTenant(data);
            setError(null);
          } else {
            console.error(`[TenantContext] Tenant config malformed for ${tenantId} — using neutral fallback. This is a configuration error.`);
            setTenant(FALLBACK_TENANT_CONFIG);
          }
        } else {
          console.error(`[TenantContext] Tenant doc not found for ${tenantId} — using neutral fallback. This is a configuration error.`);
          setTenant(FALLBACK_TENANT_CONFIG);
        }
        setIsLoading(false);
      },
      (err) => {
        console.error('[TenantContext] Tenant listener error — using neutral fallback:', err);
        setError(err.message);
        setTenant(FALLBACK_TENANT_CONFIG);
        setIsLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => {
      console.log('[TenantContext] Cleaning up tenant listener');
      unsubscribe();
    };
  }, [getTenantId]);

  /**
   * Listen for localStorage changes (e.g., user logs in with different tenant)
   */
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'tenant_id' || e.key === 'user_id') {
        console.log('[TenantContext] Storage changed, refreshing tenant');
        refreshTenant();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refreshTenant]);

  /**
   * Apply tenant theme when tenant changes
   *
   * This updates CSS custom properties to reflect the tenant's color scheme.
   * The theme is applied to document.documentElement so all components
   * using Tailwind's brand-* classes will update automatically.
   */
  useEffect(() => {
    if (tenant) {
      applyTenantTheme(tenant);
    } else {
      applyDefaultTheme();
    }
    // Cache locale + currency so getLocaleSync()/getCurrencySync() (used by
    // pure helpers + services that can't read React context) stay in sync with
    // the active tenant.
    cacheTenantLocale(tenant);
    cacheTenantCurrency(tenant);
    cacheTenantTimezone(tenant);
    cacheTenantTaxRate(tenant);
    cacheTenantFeaturePacks(tenant);
    cacheOwnerIds(tenant);
  }, [tenant]);

  // Context value
  const contextValue: TenantContextValue = {
    tenant,
    isLoading,
    error,
    refreshTenant,
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Export the context for direct access (prefer useTenant hook)
 */
export default TenantContext;
